import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import { createGlobalState } from '@vueuse/shared';
import type { RouteLocationNormalized } from 'vue-router';
import useRouteChecks from './useRouteChecks';

/**
 * Position vs History toggle for Portfolio Construction Composition.
 * Kept separate from Constituent Risk's useHistoryMode so the two pages do not share state.
 */
export const useCompositionHistoryMode = createGlobalState(() => {
  return ref(false);
});

/**
 * Position-mode view: Constituent Position routes, plus Factor Composition while its
 * History toggle is off. History-mode Composition renders the contributions view, so it
 * is categorically not a position view.
 */
export function useIsOnPositionView(route: RouteLocationNormalized): ComputedRef<boolean> {
  const { isOnPosition, isOnPortfolioConstructionComposition } = useRouteChecks(route);
  const isCompositionHistoryMode = useCompositionHistoryMode();

  return computed(
    (): boolean =>
      isOnPosition.value || (isOnPortfolioConstructionComposition.value && !isCompositionHistoryMode.value),
  );
}

/** Factor Composition with the History toggle on (contributions-style UI, not position charts). */
export function useIsFactorCompositionHistoryView(route: RouteLocationNormalized): ComputedRef<boolean> {
  const { isOnPortfolioConstructionComposition } = useRouteChecks(route);
  const isOnPositionView = useIsOnPositionView(route);

  return computed((): boolean => isOnPortfolioConstructionComposition.value && !isOnPositionView.value);
}
